import * as React from "react"
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../../lib/utils"
import { toggleVariants } from "../toggle/toggle"

interface SlidingIndicator {
  width: number
  x: number
}

const toggleGroupVariants = cva("flex items-center", {
  variants: {
    variant: {
      default: "gap-1",
      outline: "gap-1",
      pill:
        "rounded-full border border-border bg-background p-0.5 shadow-sm gap-1",
      // Opt-in sliding thumb. Existing `pill` consumers (dashboard/overview) stay
      // item-background selected; only callers that pass this variant get the indicator.
      // Muted track + tinted thumb — same language as SegmentedControl `accent="primary"`.
      // Use `primary-subtle`, never `bg-primary`: a solid fill paints over the label.
      segmented:
        "relative select-none rounded-full border border-border bg-muted/50 p-0.5 shadow-sm gap-1",
    },
    size: {
      default: "",
      sm: "",
      lg: "",
    },
  },
  defaultVariants: {
    variant: "pill",
    size: "default",
  },
})

const ToggleGroupContext = React.createContext<{
  variant: VariantProps<typeof toggleVariants>["variant"]
  size: VariantProps<typeof toggleVariants>["size"]
  sliding: boolean
}>({ variant: "default", size: "default", sliding: false })

function itemVariantForGroup(
  variant: VariantProps<typeof toggleGroupVariants>["variant"],
): VariantProps<typeof toggleVariants>["variant"] {
  if (variant === "outline") return "outline"
  if (variant === "pill" || variant === "segmented") return "pill"
  return "default"
}

function ToggleGroup({
  className,
  variant = "pill",
  size = "default",
  children,
  onValueChange,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof toggleGroupVariants>) {
  const rootRef = React.useRef<HTMLDivElement>(null)
  const [indicator, setIndicator] = React.useState<SlidingIndicator | null>(null)
  const sliding = variant === "segmented"

  const syncIndicator = React.useCallback(() => {
    const root = rootRef.current
    if (!root || !sliding) return
    const active = root.querySelector<HTMLElement>('[data-slot="toggle-group-item"][data-state="on"]')
    if (!active) {
      setIndicator(null)
      return
    }
    // offsetLeft is relative to the padding edge, which is also `left: 0` for
    // the absolutely positioned thumb. getBoundingClientRect includes the
    // border and shifts the pill onto the label.
    setIndicator({
      x: active.offsetLeft,
      width: active.offsetWidth,
    })
  }, [sliding])

  React.useLayoutEffect(() => {
    if (!sliding) return
    syncIndicator()
  }, [sliding, syncIndicator, props.value, props.defaultValue, children])

  React.useEffect(() => {
    if (!sliding) return
    const root = rootRef.current
    if (!root) return
    const observer = new ResizeObserver(() => syncIndicator())
    observer.observe(root)
    for (const item of root.querySelectorAll<HTMLElement>('[data-slot="toggle-group-item"]')) {
      observer.observe(item)
    }
    return () => observer.disconnect()
  }, [sliding, syncIndicator, children])

  return (
    <ToggleGroupContext value={{ variant: itemVariantForGroup(variant), size, sliding }}>
      <ToggleGroupPrimitive.Root
        ref={rootRef}
        data-slot="toggle-group"
        className={cn(toggleGroupVariants({ variant, size }), className)}
        {...props}
        onValueChange={(next: string | string[]) => {
          if (onValueChange) onValueChange(next as never)
          if (sliding) requestAnimationFrame(syncIndicator)
        }}
      >
        {sliding && indicator && (
          <span
            aria-hidden="true"
            // Width, not scaleX — stretching a 1px bar flattens rounded-full into square corners.
            className="pointer-events-none absolute inset-y-0.5 left-0 z-0 rounded-full bg-primary-subtle shadow-sm transition-[transform,width] duration-200 ease-out motion-reduce:transition-none"
            style={{
              transform: `translateX(${indicator.x}px)`,
              width: indicator.width,
            }}
          />
        )}
        {children}
      </ToggleGroupPrimitive.Root>
    </ToggleGroupContext>
  )
}

function ToggleGroupItem({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof toggleVariants>) {
  const context = React.use(ToggleGroupContext)
  const resolvedVariant = variant ?? context.variant
  const resolvedSize = size ?? context.size

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      className={cn(
        toggleVariants({ variant: resolvedVariant, size: resolvedSize }),
        resolvedVariant === "pill" && "rounded-full px-2.5",
        context.sliding &&
          "relative z-10 text-muted-foreground hover:text-foreground data-[state=on]:bg-transparent data-[state=on]:text-primary-subtle-foreground data-[state=on]:shadow-none",
        className,
      )}
      {...props}
    />
  )
}

export { ToggleGroup, ToggleGroupItem }
